import {
  Component,
  HostListener,
  Input,
  OnDestroy,
  OnChanges,
  OnInit,
  Renderer2,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { Link } from '../../../interfaces/shared-interface';
import { NAV_SIDE_DATA } from '../nav-data';

@Component({
  selector: 'app-nav-header',
  templateUrl: './nav-header.component.html',
  styleUrl: './nav-header.component.scss',
  standalone: false,
})
export class NavHeaderComponent implements OnInit, OnChanges, OnDestroy {
  @Input() expertisesList: string[] = [];
  public navbarList: Link[] = NAV_SIDE_DATA;
  public isMenuOpen = false;
  public currentTitle: string = '';
  private titleIndex = 0;
  private intervalId: any;
  public isFadingOut = false;
  public isFadingIn = false;
  public isHomePage = true;
  public isMobileMenuActive = false;
  public isSearchOpen = false;
  public isNavVisible = true;
  private lastScrollY = 0;

  private unsubscribe$ = new Subject<void>();

  constructor(
    private renderer: Renderer2,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.startTitleRotation();
    this.checkRouterPath();
    this.router.events.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.checkRouterPath();
      this.closeSearch();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['expertisesList']) {
      this.titleIndex = 0;
      this.startTitleRotation();
    }
  }

  private startTitleRotation(): void {
    this.stopTitleRotation();

    if (!this.expertisesList.length) {
      this.currentTitle = '';
      return;
    }

    this.currentTitle = this.expertisesList[this.titleIndex];
    this.intervalId = setInterval(() => {
      this.isFadingOut = true;
      setTimeout(() => {
        this.titleIndex = (this.titleIndex + 1) % this.expertisesList.length;
        this.currentTitle = this.expertisesList[this.titleIndex];
        this.isFadingOut = false;
        this.isFadingIn = true;
      }, 500);
      setTimeout(() => {
        this.isFadingIn = false;
      }, 1000);
    }, 3000);
  }

  private stopTitleRotation(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  @HostListener('window:scroll', ['$event'])
  public onScroll(event: Event): void {
    const navHome = document.querySelector('.top-nav-container') as HTMLElement;
    const nav = document.querySelector(
      '.top-nav-mobile-container'
    ) as HTMLElement;
    const currentUrl = this.router.url;
    const currentScrollY = Math.max(window.scrollY || 0, 0);
    const scrolledDown = currentScrollY > this.lastScrollY;
    const scrolledUp = currentScrollY < this.lastScrollY;

    // Keep nav visible at top; hide on downward scroll and reveal on upward scroll.
    if (currentScrollY <= 0 || currentScrollY < 8) {
      this.isNavVisible = true;
    } else if (!this.isMenuOpen && !this.isSearchOpen) {
      if (scrolledDown && currentScrollY > 80) {
        this.isNavVisible = false;
      } else if (scrolledUp) {
        this.isNavVisible = true;
      }
    } else {
      // Keep nav visible while overlays are open.
      this.isNavVisible = true;
    }

    this.lastScrollY = currentScrollY;

    if (currentUrl === '/careers' || currentUrl === '/expertises') {
      if (window.scrollY > window.innerHeight - 100) {
        navHome.classList.add('careers-nav-scrolled');
        nav.classList.add('careers-nav-scrolled');
      } else {
        navHome.classList.remove('careers-nav-scrolled');
        nav.classList.remove('careers-nav-scrolled');
      }

      navHome.classList.remove('bg-scroll');
      nav.classList.remove('bg-scroll');
      return;
    }

    navHome.classList.remove('careers-nav-scrolled');
    nav.classList.remove('careers-nav-scrolled');

    if (
      currentUrl === '/expertises' ||
      currentUrl === '/about-us' ||
      currentUrl === '/projects' ||
      currentUrl === '/news' ||
      currentUrl === '/insights' ||
      currentUrl === '/contact' ||
      currentUrl === '/' ||
      currentUrl.startsWith('/projects/project') ||
      currentUrl.startsWith('/about-us/about') ||
      currentUrl.startsWith('/expertises/expertise') ||
      currentUrl.startsWith('/news/articles') ||
      currentUrl.startsWith('/insights/') ||
      currentUrl.startsWith('/careers/career')
    ) {
      if (window.scrollY > 50) {
        navHome.classList.add('bg-scroll');
        nav.classList.add('bg-scroll');
      } else {
        navHome.classList.remove('bg-scroll');
        nav.classList.remove('bg-scroll');
      }
    }
  }

  public toggleMenu(): void {
    if (this.isSearchOpen) {
      this.closeSearch();
    }

    this.isMobileMenuActive = false;
    setTimeout(() => {
      this.isMobileMenuActive = true;
    }, 100);
    this.isMenuOpen = !this.isMenuOpen;
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (this.isMenuOpen) {
      this.renderer.setStyle(document.body, 'overflow', 'hidden');
      if (themeColorMeta) {
        themeColorMeta.setAttribute('content', '#cdd315');
      }
    } else {
      this.renderer.removeStyle(document.body, 'overflow');
      if (themeColorMeta) {
        themeColorMeta.setAttribute('content', '#ffffff');
      }
    }
  }

  private checkRouterPath(): void {
    const navHome = document.querySelector('.top-nav-container') as HTMLElement;
    const nav = document.querySelector(
      '.top-nav-mobile-container'
    ) as HTMLElement;
    const currentUrl = this.router.url;
    const path = currentUrl.split('?')[0];
    this.isHomePage = !(
      path === '/' ||
      path.includes('/contact') ||
      path.includes('/careers') ||
      path.includes('/expertises') ||
      path.includes('/insights') ||
      path.includes('/projects/project')
    );
    navHome.classList.remove('bg-scroll');
    nav.classList.remove('bg-scroll');
    navHome.classList.remove('careers-nav-scrolled');
    nav.classList.remove('careers-nav-scrolled');
    this.lastScrollY = Math.max(window.scrollY || 0, 0);

    if (this.lastScrollY <= 0) {
      this.isNavVisible = true;
    }
  }

  public openSearch(): void {
    if (this.isMenuOpen) {
      this.toggleMenu();
    }

    this.isSearchOpen = true;
    this.renderer.setStyle(document.body, 'overflow', 'hidden');
  }

  public closeSearch(): void {
    this.isSearchOpen = false;

    if (!this.isMenuOpen) {
      this.renderer.removeStyle(document.body, 'overflow');
    }
  }

  ngOnDestroy() {
    this.stopTitleRotation();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
